import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { getChatbotUrl, getDashboardUrl } from './runtime-config';
import { readStoredAuthToken, readStoredOrgId } from './auth.service';

export type OrderWorkflowStatus = 'placed' | 'confirmed' | 'paid' | 'fulfilled' | 'rejected';

export interface OrderApiItemLine {
  name: string;
  quantity: number;
  price: number;
}

export interface OrderApiItem {
  orderId: string;
  customerName: string;
  displayName: string;
  phone: string;
  currency: string;
  items: OrderApiItemLine[];
  total: number;
  status: OrderWorkflowStatus;
  createdAt: string;
}

type UnknownMap = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class OrdersApiService {
  private readonly base = `${getDashboardUrl()}/api/webapp/orders`;
  private readonly orderStatusEndpoint = `${getChatbotUrl()}/api/dashboard/order/status`;

  constructor(private readonly http: HttpClient) {}

  async listOrders(): Promise<OrderApiItem[]> {
    const orgId = readStoredOrgId();
    const payload = await firstValueFrom(
      this.http.get<unknown>(`${this.base}/${encodeURIComponent(orgId)}`, { headers: this.buildHeaders() })
    );
    const rows = this.extractRows(payload);
    return rows
      .map(row => this.mapOrder(row))
      .filter((row): row is OrderApiItem => !!row);
  }

  async notifyStatusChange(order: OrderApiItem, status: OrderWorkflowStatus): Promise<void> {
    const from = readStoredOrgId();
    const to = (order.phone || '').trim();
    if (!to || to === '-') {
      throw new Error('Missing destination phone number for order status update.');
    }

    const payload = {
      from,
      orderId: order.orderId,
      to: to.replace(/\D/g, ''),
      status,
    };

    await firstValueFrom(
      this.http.post(this.orderStatusEndpoint, payload, {
        headers: this.buildHeaders(),
      })
    );
  }

  mapOrder(payload: unknown): OrderApiItem | null {
    if (!payload || typeof payload !== 'object') return null;
    const row = payload as UnknownMap;

    const orderId = this.readString(row, ['orderId', 'id']);
    if (!orderId) return null;

    const displayName = this.readString(row, ['displayName', 'customerName', 'clientName', 'name', 'contactName']) || '';
    const phone = this.readString(row, ['clientPhonenumber', 'phone', 'phoneNumber', 'customerPhone']) || '-';
    const customerName = phone || this.readString(row, ['clientId', 'customerId']) || 'Unknown';
    const amountWithCurrency = this.readString(row, ['amount']);
    const parsedAmount = this.parseAmountCurrency(amountWithCurrency);
    const currency = this.readString(row, ['currency']) || parsedAmount.currency || 'USD';
    const createdAt = this.readString(row, ['createdAt', 'createdOn', 'timestamp', 'orderDate']) || new Date().toISOString();
    const statusRaw = this.readString(row, ['status', 'orderStatus', 'stage']);
    const status = this.normalizeStatus(statusRaw);

    const items = this.readItems(row, currency);
    const totalFromPayload = this.readNumber(row, ['total', 'orderValue', 'amount', 'grandTotal']);
    const amountNumber = parsedAmount.amount;
    const total = totalFromPayload > 0
      ? totalFromPayload
      : amountNumber > 0
        ? amountNumber
        : items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    return { orderId, customerName, displayName, phone, currency, items, total, status, createdAt };
  }

  private readItems(row: UnknownMap, currency: string): OrderApiItemLine[] {
    const rawItems = this.readItemsArray(row);
    if (!rawItems.length) {
      const fallbackName = this.readString(row, ['selectedType']) || 'Item';
      const fallbackPrice = this.readNumber(row, ['price', 'amount']);
      if (!fallbackPrice) return [];
      return [{ name: fallbackName, quantity: 1, price: fallbackPrice }];
    }
    const out: OrderApiItemLine[] = [];

    for (const raw of rawItems) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as UnknownMap;
      const name = this.readString(item, ['name', 'itemName', 'productName']) || 'Item';
      const quantity = this.readNumber(item, ['quantity', 'qty', 'count']) || 1;
      const priceRaw = this.readString(item, ['amount']);
      const parsedAmount = this.parseAmountCurrency(priceRaw);
      const price = this.readNumber(item, ['price', 'unitPrice', 'amount']) || parsedAmount.amount || 0;
      out.push({ name, quantity, price });
    }

    return out;
  }

  private readItemsArray(row: UnknownMap): unknown[] {
    const candidates = [
      row['items'],
      row['orderItems'],
      row['products'],
      row['productDetails'],
      row['selectedItems'],
      row['selectedProducts'],
      row['orderProducts'],
    ];
    for (const value of candidates) {
      if (Array.isArray(value)) return value;
    }
    return [];
  }

  private extractRows(payload: unknown): unknown[] {
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === 'object') {
      const source = payload as UnknownMap;
      if (Array.isArray(source['data'])) return source['data'];
      if (Array.isArray(source['items'])) return source['items'];
      if (Array.isArray(source['orders'])) return source['orders'];
    }
    return [];
  }

  private buildHeaders(): HttpHeaders {
    const token = readStoredAuthToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return new HttpHeaders(headers);
  }

  private readString(source: UnknownMap, keys: string[]): string {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
      if (typeof value === 'number') return String(value);
    }
    return '';
  }

  private readNumber(source: UnknownMap, keys: string[]): number {
    for (const key of keys) {
      const value = Number(source[key]);
      if (Number.isFinite(value)) return value;
    }
    return 0;
  }

  private normalizeStatus(raw: string): OrderWorkflowStatus {
    const norm = raw.toLowerCase();
    if (norm.includes('draft') || norm.includes('new') || norm.includes('open')) return 'placed';
    if (norm.includes('reject') || norm.includes('cancel') || norm.includes('fail')) return 'rejected';
    if (norm.includes('fulfill') || norm.includes('deliver') || norm.includes('complete')) return 'fulfilled';
    if (norm.includes('paid') || norm.includes('payment')) return 'paid';
    if (norm.includes('confirm') || norm.includes('approved')) return 'confirmed';
    return 'placed';
  }

  private parseAmountCurrency(value: string): { amount: number; currency: string } {
    if (!value) return { amount: 0, currency: '' };
    const trimmed = value.trim();
    const amountMatch = trimmed.match(/-?\\d+(?:\\.\\d+)?/);
    const amount = amountMatch ? Number(amountMatch[0]) : 0;
    const parts = trimmed.split(/\\s+/).filter(Boolean);
    const currencyCandidate = parts.find(part => /^[A-Za-z]{3}$/.test(part)) || '';
    return {
      amount: Number.isFinite(amount) ? amount : 0,
      currency: currencyCandidate.toUpperCase(),
    };
  }
}
